import { z } from "zod";
import { payoutMethodSchema } from "./auth.js";
import { responseSchema } from "../http.js";
import { orderProofInputSchema, proofMimeSchema } from "./order.js";
import { commissionSchema } from "./commission.js";
import { currencySchema, priceCentsSchema } from "./product.js";
/**
 * ═══════════════════════════════════════════════════════════
 * El pago de comisiones — el expediente por persona
 *
 * (Anahí, 27-ago, sobre la pantalla del sistema viejo): clic en una
 * comisión del libro → la PÁGINA de esa persona, con todos sus
 * movimientos explicados, cuánto se le debe, su cuenta bancaria (o el
 * aviso de que no la tiene), PAGAR TODO con comprobante, y anular lo
 * que no proceda.
 *
 * El comprobante del pago viaja como el de las órdenes: base64 en
 * JSON, foto o PDF, 4 MB.
 * ═══════════════════════════════════════════════════════════
 */
export const payoutSchema = z.object({
    id: z.string(),
    userId: z.string(),
    totalCents: priceCentsSchema,
    currency: currencySchema,
    commissionCount: z.number().int().min(1),
    /** Qué comisiones liquidó ESTE pago — la tabla enlaza cada una con
     * su comprobante por aquí. */
    commissionIds: z.array(z.string()),
    createdAt: z.iso.datetime(),
    proof: z
        .object({
        fileName: z.string(),
        mimeType: proofMimeSchema,
        sizeBytes: z.number().int().min(0),
        /** Listo para abrir/descargar. Solo en el detalle por persona. */
        dataUrl: z.string().optional(),
    })
        .optional(),
});
/**
 * Pagar lo debido. El comprobante es opcional pero recomendable: es la
 * prueba de la transferencia.
 *
 * ── Todo, o comisión por comisión (Anahí, 29-ago) ────────────────────
 * "Debe poder pagar comisión por comisión, y se debe ir haciendo el
 * descuento de la deuda."
 *
 * Sin `commissionIds` se paga TODO lo pendiente, como hasta ahora. Con
 * ellos, solo esas — y el resto sigue debiéndose, que es lo que permite
 * ir abonando. Un endpoint distinto para el pago suelto habría
 * duplicado el comprobante, el registro del payout y el candado de "eso
 * ya está pagado"; aquí es el mismo camino con un filtro más.
 */
export const adminPayUserRequestSchema = z.object({
    proof: orderProofInputSchema.optional(),
    /** Cuáles pagar. Vacío o ausente: todo lo que se deba EN `currency`. */
    commissionIds: z.array(z.string().trim().min(1)).optional(),
    /**
     * En qué moneda se paga. Un pago es UNA transferencia en UNA moneda:
     * sin esto, "pagar todo" a un socio con deuda en pesos y en dólares
     * sumaba las dos cifras en un solo payout (bug cerrado el 14-sep-2026).
     * Ausente: la única moneda con deuda, o error si hay dos.
     */
    currency: currencySchema.optional(),
});
export const adminUserCommissionsResponseSchema = responseSchema(z.object({
    profile: z.object({
        userId: z.string(),
        name: z.string(),
        email: z.string(),
        /** "Distribuidor", si tiene posición en la red. */
        customerType: z.string().nullable(),
        /** Quién lo invitó, si alguien. */
        sponsorName: z.string().nullable(),
        hasPayoutMethod: z.boolean(),
        payoutMethod: payoutMethodSchema.nullable(),
    }),
    /**
     * Un renglón por MONEDA con movimiento (14-sep-2026): lo debido y lo
     * pagado no se mezclan entre pesos y dólares. Siempre hay al menos
     * uno — la moneda del país del socio, aunque esté en cero.
     */
    balances: z
        .array(z.object({
        currency: currencySchema,
        /** Lo DEBIDO: pendiente + liberado. Es el "POR PAGAR". */
        owedCents: priceCentsSchema,
        paidCents: priceCentsSchema,
    }))
        .min(1),
    items: z.array(commissionSchema),
    /** `userId → nombre` de quienes generaron cada comisión. */
    names: z.record(z.string(), z.string()),
    /**
     * `orderId → fecha de la compra` (22-oct-2026): la fecha que enseña la
     * tabla es la de la compra que generó la comisión, no la del corte.
     */
    purchaseDates: z.record(z.string(), z.string()).optional(),
    payouts: z.array(payoutSchema),
}));
export const payoutResponseSchema = responseSchema(z.object({ payout: payoutSchema, paidCount: z.number().int().min(1) }));
export const PAYOUT_ROUTES = {
    adminUser: (userId) => `/api/admin/commissions/user/${userId}`,
    adminPayUser: (userId) => `/api/admin/commissions/user/${userId}/pay`,
    adminAnnul: (commissionId) => `/api/admin/commissions/${commissionId}/annul`,
};
//# sourceMappingURL=payout.js.map